import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Api from '../../../api';
import TalentNavbar from '../../../components/TalentNavbar';
import TalentBackButton from '../../../components/TalentBackButton/TalentBackButton';
import { Analytics } from '../../../utils/analytics';
import { openMailtoUrl } from '../../../utils/mailto';
import styles from './CoursePlayerPage.module.scss';

const LMS_CAPACITY_MAILTO =
  'mailto:help@tempstars.com?subject=LMS%20limit%20-%20I%20can%27t%20access%20the%20courses';

type LocationState = { lmsCourseId: number };
type CourseProgressResult = {
  restrictionLifted?: boolean;
  membershipRecoveryTransition?: { transitioned?: boolean };
} | null | undefined;

const AUTOLOGIN_REDIRECT_MS = 2500;

const isCordova = () => typeof (window as any).cordova !== 'undefined';

const openExternalUrl = (url: string) => {
  const cordova = (window as any).cordova;
  if (cordova?.InAppBrowser?.open) {
    cordova.InAppBrowser.open(url, '_system', 'location=yes');
    return;
  }
  window.open(url, '_system');
};

const openLmsCapacitySupport = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
  openMailtoUrl(LMS_CAPACITY_MAILTO, {
    to: 'help@tempstars.com',
    subject: "LMS limit - I can't access the courses",
  });
};

const didCompleteMembershipRecovery = (result: CourseProgressResult): boolean =>
  !!(result?.restrictionLifted || result?.membershipRecoveryTransition?.transitioned);

const useTalentNavContext = () => {
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const hygienist = currentUser?.hygienist;
  const HYGIENIST_TYPES = (window as any).TempStars?.HYGIENIST_TYPES;

  return {
    firstName: hygienist?.firstName || '',
    lastName: hygienist?.lastName || '',
    photoUrl: hygienist?.photoUrl || undefined,
    isHygOrAsst: hygienist?.type === HYGIENIST_TYPES?.HYGIENIST
      || hygienist?.type === HYGIENIST_TYPES?.ASSISTANT,
    instabookActivated: hygienist?.InstabookActivated === 1,
    isTestAccount: (hygienist as any)?.isTest === 1,
  };
};

type CoursePlayerShellProps = {
  onBack: () => void;
  children: React.ReactNode;
};

const CoursePlayerShell: React.FC<CoursePlayerShellProps> = ({ onBack, children }) => {
  const nav = useTalentNavContext();

  return (
    <div className={styles.playerContainer}>
      <TalentNavbar
        {...nav}
        showAddShiftButton={false}
        showJobBoardButton={false}
      />
      <TalentBackButton onBack={onBack} label="Back to Courses" />
      <div className={styles.contentArea}>{children}</div>
    </div>
  );
};

const CoursePlayerPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const courseId = location.state?.lmsCourseId;
  const [launchUrl, setLaunchUrl] = React.useState<string | undefined>(undefined);
  const [courseEntryUrl, setCourseEntryUrl] = React.useState<string | undefined>(undefined);
  const [launchViaAutologin, setLaunchViaAutologin] = React.useState(false);
  const awaitingAutologinRedirectRef = React.useRef(false);
  const [directLaunchUrl, setDirectLaunchUrl] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [retryNonce, setRetryNonce] = React.useState<number>(0);
  const [showRestrictionLiftedModal, setShowRestrictionLiftedModal] =
    React.useState<boolean>(false);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  const goBackToCourses = React.useCallback(() => {
    history.push({ pathname: 'coursesHome' });
  }, [history]);

  const refreshProgressAndExit = React.useCallback(async () => {
    if (!courseId) return;
    try {
      await Api.updateToken();
      const result = await Api.getCourseProgress(courseId);
      if (didCompleteMembershipRecovery(result)) {
        setShowRestrictionLiftedModal(true);
        return;
      }
    } catch (e) {
      // ignore
    }
    goBackToCourses();
  }, [courseId, goBackToCourses]);

  React.useEffect(() => {
    if (!courseId) {
      setError('Missing course');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    setLaunchUrl(undefined);
    setCourseEntryUrl(undefined);
    setLaunchViaAutologin(false);
    awaitingAutologinRedirectRef.current = false;
    setDirectLaunchUrl(undefined);

    Api.updateToken()
      .then(() => Api.getCourseLaunchUrl(courseId))
      .then((response) => {
        if (cancelled) return;
        setDirectLaunchUrl(response.directUrl);

        console.info('[CoursePlayer] Launch URL received:', {
          embedAllowed: response.embedAllowed,
          launchViaAutologin: response.launchViaAutologin,
          hasCourseEntryUrl: !!(response.courseEntryUrl || response.courseUrl),
          reason: response.reason,
        });

        if (response.embedAllowed === false) {
          console.error('[CoursePlayer] Embedding not allowed:', response.reason);
          setError(`Embedding not allowed: ${response.reason || 'Unknown reason'}`);
          return;
        }

        const entryUrl = response.courseEntryUrl || response.courseUrl;
        const usesAutologin = !!(response.launchViaAutologin && entryUrl);
        setLaunchViaAutologin(usesAutologin);
        if (usesAutologin) {
          console.info('[CoursePlayer] Autologin launch enabled');
          setCourseEntryUrl(entryUrl);
          awaitingAutologinRedirectRef.current = true;
        } else {
          console.info('[CoursePlayer] Loading SSO URL in iframe');
        }
        setLaunchUrl(response.url);
      })
      .catch((err) => {
        console.error('[CoursePlayer] Failed to get launch URL:', err?.body?.error?.code || err?.message || err);
        const code = err?.body?.error?.code;
        const message = err?.body?.error?.message || err?.userMessage || 'Failed to get launch URL';
        if (!cancelled) {
          setErrorCode(code || null);
          setError(code ? `${message} (${code})` : message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [courseId, retryNonce]);

  React.useEffect(() => {
    if (!launchUrl || !courseEntryUrl || !awaitingAutologinRedirectRef.current) {
      return undefined;
    }
    const target = courseEntryUrl;
    const timer = window.setTimeout(() => {
      if (!awaitingAutologinRedirectRef.current || !iframeRef.current) {
        return;
      }
      awaitingAutologinRedirectRef.current = false;
      iframeRef.current.src = target;
      console.info('[CoursePlayer] Autologin redirect to course entry');
    }, AUTOLOGIN_REDIRECT_MS);
    return () => window.clearTimeout(timer);
  }, [launchUrl, courseEntryUrl]);

  React.useEffect(() => {
    if (!isCordova() || !launchUrl || !courseEntryUrl || !launchViaAutologin) {
      return undefined;
    }
    openExternalUrl(launchUrl);
    const timer = window.setTimeout(() => {
      openExternalUrl(courseEntryUrl);
    }, AUTOLOGIN_REDIRECT_MS);
    return () => window.clearTimeout(timer);
  }, [launchUrl, courseEntryUrl, launchViaAutologin]);

  React.useEffect(() => {
    if (!launchUrl) return;
    if (isCordova()) return;
    if (!courseId) return;
    if (showRestrictionLiftedModal) return;
    const id = window.setInterval(async () => {
      try {
        await Api.updateToken();
        const result = await Api.getCourseProgress(courseId);
        if (didCompleteMembershipRecovery(result)) {
          setShowRestrictionLiftedModal(true);
        }
      } catch (e) { /* ignore */ }
    }, 45000);
    return () => window.clearInterval(id);
  }, [launchUrl, courseId, showRestrictionLiftedModal]);

  React.useEffect(() => {
    if (errorCode !== 'LMS_CAPACITY_REACHED' || !courseId) return;
    Analytics.track('Course Launch Blocked - LMS Capacity', {
      lmsCourseId: courseId,
      platform: isCordova() ? 'mobile' : 'web',
    });
  }, [errorCode, courseId]);

  if (errorCode === 'LMS_CAPACITY_REACHED') {
    return (
      <CoursePlayerShell onBack={goBackToCourses}>
        <div className={styles.iframeWrapper}>
          <div className={styles.capacityNotice}>
            <div className={styles.capacityIcon} aria-hidden="true">
              <SchoolIcon />
            </div>
            <p className={styles.capacityMessage}>
              This course is not currently available, please{' '}
              <a
                href={LMS_CAPACITY_MAILTO}
                className={styles.capacityLink}
                onClick={openLmsCapacitySupport}
              >
                contact
              </a>
              {' '}the TempStars Support Team and they will help.
            </p>
            <button
              type="button"
              className={styles.capacityBackButton}
              onClick={goBackToCourses}
            >
              Back to Courses
            </button>
          </div>
        </div>
      </CoursePlayerShell>
    );
  }

  if (loading) {
    return (
      <CoursePlayerShell onBack={goBackToCourses}>
        <div className={styles.iframeWrapper}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner} />
            <div className={styles.loadingTitle}>Loading course...</div>
            <div className={styles.loadingMessage}>Preparing your learning experience</div>
          </div>
        </div>
      </CoursePlayerShell>
    );
  }

  if (error) {
    return (
      <CoursePlayerShell onBack={goBackToCourses}>
        <div className={styles.iframeWrapper}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon} aria-hidden="true">
              <ErrorOutlineIcon />
            </div>
            <div className={styles.errorTitle}>Unable to Load Course</div>
            <div className={styles.errorMessage}>{error}</div>
            <button className={styles.errorButton} onClick={() => setRetryNonce(value => value + 1)}>
              Retry
            </button>
            {directLaunchUrl && (
              <a
                href={directLaunchUrl}
                rel="noreferrer"
                target="_blank"
                className={styles.errorButton}
                style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}
              >
                Try direct launch
              </a>
            )}
            <button className={styles.errorButton} onClick={goBackToCourses}>
              Back to Courses
            </button>
          </div>
        </div>
      </CoursePlayerShell>
    );
  }

  if (!courseId) {
    return (
      <CoursePlayerShell onBack={goBackToCourses}>
        <div className={styles.iframeWrapper}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon} aria-hidden="true">
              <ErrorOutlineIcon />
            </div>
            <div className={styles.errorTitle}>Missing Course</div>
            <div className={styles.errorMessage}>No course was selected</div>
            <button className={styles.errorButton} onClick={goBackToCourses}>
              Back to Courses
            </button>
          </div>
        </div>
      </CoursePlayerShell>
    );
  }

  if (isCordova()) {
    const manualOpenUrl = launchViaAutologin && courseEntryUrl ? courseEntryUrl : launchUrl;
    return (
      <CoursePlayerShell onBack={refreshProgressAndExit}>
        <div className={styles.iframeWrapper}>
          <div className={styles.loadingState}>
            <div className={styles.loadingTitle}>Opening course...</div>
            <div className={styles.loadingMessage}>
              {launchViaAutologin
                ? 'Signing you in and opening your course. If it does not open automatically, tap below.'
                : 'If not opened automatically, tap below:'}
            </div>
            {manualOpenUrl && (
              <a
                href={manualOpenUrl}
                rel="noreferrer"
                target="_system"
                className={styles.errorButton}
                style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}
                onClick={(event) => {
                  event.preventDefault();
                  openExternalUrl(manualOpenUrl);
                }}
              >
                Open Course
              </a>
            )}
            <button className={styles.errorButton} onClick={refreshProgressAndExit} style={{ marginTop: '1rem' }}>
              Back to Courses
            </button>
          </div>
        </div>
      </CoursePlayerShell>
    );
  }

  return (
    <CoursePlayerShell onBack={refreshProgressAndExit}>
      <div className={styles.iframeWrapper}>
        {launchUrl ? (
          <iframe
            ref={iframeRef}
            title="Course Player"
            src={launchUrl}
            className={styles.iframe}
            allow="fullscreen; autoplay; clipboard-read; clipboard-write"
            onLoad={() => {
              console.info('[CoursePlayer] iframe load event');
            }}
            onError={(e) => {
              console.error('[CoursePlayer] iframe error:', e);
            }}
          />
        ) : (
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner} />
            <div className={styles.loadingTitle}>Loading course...</div>
            <div className={styles.loadingMessage}>Please wait while we prepare your course</div>
          </div>
        )}
      </div>

      {showRestrictionLiftedModal && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalCard}>
            <div className={styles.modalIcon}>✓</div>
            <h2 className={styles.modalTitle}>Course Complete!</h2>
            <p className={styles.modalMessage}>
              Thanks for completing the course. Your TempStars status has been updated and you can review it in My Stats.
            </p>
            <button
              className={styles.modalButton}
              onClick={() => {
                setShowRestrictionLiftedModal(false);
                history.push({ pathname: 'myStats' });
              }}
            >
              View My Stats
            </button>
          </div>
        </div>
      )}
    </CoursePlayerShell>
  );
};

export default CoursePlayerPage;
